import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, TrendingUp, Clock } from "lucide-react";
import { api, inr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RT_NAV, allowedNav } from "@/nav";
import { Card, PageHeader, DataTable, txnColumns, ReceiptDialog } from "@/components/shared";

const QUICK = ["mobile-recharge", "dth-recharge", "bbps", "dmt", "aeps", "aadhaar-pay", "cash-deposit", "move-to-bank", "fund-request", "credit-card", "pan-verify", "qr-collect"];

export default function Dashboard() {
  const { user, refreshBalance } = useAuth();
  const [d, setD] = useState(null);
  const [rec, setRec] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setD(data));
    refreshBalance();
  }, [refreshBalance]);

  const items = allowedNav(RT_NAV, user.services).flatMap((g) => g.items).filter((i) => QUICK.includes(i.id));
  const todayTotal = (d?.today || []).reduce((s, x) => s + x.amount, 0);
  const todayComm = (d?.today || []).reduce((s, x) => s + x.commission, 0);

  return (
    <div>
      <PageHeader title={`Namaste, ${user.name.split(" ")[0]}`} subtitle="Here's what's happening at your counter today." />
      {d?.news?.length > 0 && (
        <div className="mb-6 flex items-center gap-3 overflow-hidden rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900" data-testid="news-ticker">
          <Megaphone className="h-4 w-4 shrink-0" />
          <div className="overflow-hidden whitespace-nowrap"><div className="ticker inline-block">{d.news.map((n) => n.news).join("   •   ")}</div></div>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl bg-gradient-to-br from-[#0D1B3E] to-[#0B1329] p-5 text-white fade-up" data-testid="dash-main-wallet">
          <div className="text-[11px] font-semibold uppercase tracking-widest opacity-80">Main Wallet</div>
          <div className="mt-2 font-mono text-3xl font-semibold">{inr(d?.balance)}</div>
          {user.services?.fund_request !== false && <Link to="/fund-request" className="mt-3 inline-block text-xs font-semibold underline-offset-2 hover:underline" data-testid="dash-add-money-link">+ Add money</Link>}
        </div>
        <div className="rounded-xl bg-gradient-to-br from-emerald-600 to-teal-800 p-5 text-white fade-up" data-testid="dash-aeps-wallet">
          <div className="text-[11px] font-semibold uppercase tracking-widest opacity-80">AEPS Wallet</div>
          <div className="mt-2 font-mono text-3xl font-semibold">{inr(d?.aeps_balance)}</div>
          {user.services?.payout !== false && <Link to="/move-to-bank" className="mt-3 inline-block text-xs font-semibold hover:underline" data-testid="dash-move-bank-link">Move to bank →</Link>}
        </div>
        <Card className="!p-5">
          <div className="np-label flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" />Today's business</div>
          <div className="mt-1 font-mono text-2xl font-semibold" data-testid="dash-today-amount">{inr(todayTotal)}</div>
          <div className="mt-1 text-xs text-slate-500">Commission earned <b className="text-emerald-700">{inr(todayComm)}</b></div>
        </Card>
        <Card className="!p-5">
          <div className="np-label flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />This month</div>
          <div className="mt-1 font-mono text-2xl font-semibold" data-testid="dash-month-commission">{inr(d?.month?.commission)}</div>
          <div className="mt-1 text-xs text-slate-500">{d?.month?.count || 0} transactions · {d?.pending_funds || 0} fund requests pending</div>
        </Card>
      </div>

      <h2 className="mb-3 mt-8 font-display text-lg font-semibold">Services</h2>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid="services-grid">
        {items.map((it, i) => (
          <Link key={it.id} to={it.to} style={{ animationDelay: `${i * 30}ms` }}
            className="group np-card flex flex-col items-center gap-2 p-4 text-center fade-up transition-[transform,border-color] hover:-translate-y-0.5 hover:border-blue-300"
            data-testid={`service-tile-${it.id}`}>
            <div className="grid h-11 w-11 place-items-center rounded-lg bg-slate-100 text-[#0D1B3E] transition-colors group-hover:bg-[#0D1B3E] group-hover:text-white"><it.icon className="h-5 w-5" /></div>
            <span className="text-xs font-semibold">{it.label}</span>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid gap-4 xl:grid-cols-[2fr_1fr]">
        <Card title="Recent transactions" action={<Link to="/reports/all" className="text-xs font-semibold text-[#0D1B3E]" data-testid="view-all-txns-link">View all</Link>}>
          <DataTable columns={txnColumns(setRec).filter((c) => !["TDS", "Charge", "Operator"].includes(c.label))} rows={d?.recent || []} testid="recent-txns-table" />
        </Card>
        <Card title="Today by service">
          <div className="space-y-2" data-testid="today-by-service">
            {(d?.today || []).length === 0 && <div className="text-sm text-slate-400">No transactions yet today.</div>}
            {(d?.today || []).map((t) => (
              <div key={t.service} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm">
                <span className="font-medium">{t.service} <span className="text-xs text-slate-400">×{t.count}</span></span>
                <span className="font-mono font-semibold">{inr(t.amount)}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 np-label">Notifications</div>
          {(d?.news || []).map((n) => (
            <div key={n.id} className="border-b border-slate-100 py-2 text-sm last:border-0">{n.news}<div className="text-[11px] text-slate-400">{fmtDate(n.created_at)}</div></div>
          ))}
        </Card>
      </div>
      <ReceiptDialog txn={rec} onClose={() => setRec(null)} />
    </div>
  );
}
